import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback
} from 'react';

import {
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  updateEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';

import { auth } from '../config/firebase';

const initialState = {
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false
};

const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  LOGOUT: 'LOGOUT'
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
        error: null
      };

    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };

    default:
      return state;
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    let unsubscribe;

    const initializeAuth = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);

        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (user) {
            try {
              const token = await user.getIdToken();

              localStorage.setItem('firebaseToken', token);

              const userData = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                emailVerified: user.emailVerified
              };

              localStorage.setItem(
                'user',
                JSON.stringify(userData)
              );

              dispatch({
                type: AUTH_ACTIONS.SET_USER,
                payload: userData
              });
            } catch (error) {
              console.error('Error getting user token:', error);

              dispatch({
                type: AUTH_ACTIONS.SET_ERROR,
                payload: 'Authentication error'
              });
            }
          } else {
            localStorage.removeItem('firebaseToken');
            localStorage.removeItem('user');

            dispatch({
              type: AUTH_ACTIONS.SET_USER,
              payload: null
            });
          }
        });
      } catch (error) {
        console.error('Firebase persistence initialization error:', error);

        dispatch({
          type: AUTH_ACTIONS.SET_ERROR,
          payload: 'Authentication initialization failed'
        });
      }
    };

    initializeAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const login = async (email, password) => {
    try {
      dispatch({
        type: AUTH_ACTIONS.SET_LOADING,
        payload: true
      });

      dispatch({
        type: AUTH_ACTIONS.CLEAR_ERROR
      });

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const token = await userCredential.user.getIdToken(true);

      localStorage.setItem('firebaseToken', token);

      const userData = {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL,
        emailVerified: userCredential.user.emailVerified
      };

      localStorage.setItem(
        'user',
        JSON.stringify(userData)
      );

      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: userData
      });

      return {
        success: true
      };
    } catch (error) {
      const errorMessage =
        error.code === 'auth/invalid-credential'
          ? 'Invalid email or password'
          : error.code === 'auth/too-many-requests'
          ? 'Too many failed attempts. Please try again later'
          : 'An error occurred during login';

      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: errorMessage
      });

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);

      localStorage.removeItem('firebaseToken');
      localStorage.removeItem('user');

      dispatch({
        type: AUTH_ACTIONS.LOGOUT
      });
    } catch (error) {
      console.error('Logout error:', error);

      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: 'Logout failed'
      });
    }
  };

  const clearError = useCallback(() => {
    dispatch({
      type: AUTH_ACTIONS.CLEAR_ERROR
    });
  }, []);

  const getToken = async () => {
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }

    return null;
  };

  const updateProfileDetails = async ({
    displayName,
    email,
    phone
  }) => {
    try {
      dispatch({
        type: AUTH_ACTIONS.SET_LOADING,
        payload: true
      });

      dispatch({
        type: AUTH_ACTIONS.CLEAR_ERROR
      });

      const user = auth.currentUser;

      if (!user) {
        throw new Error('Not authenticated');
      }

      if (displayName) {
        await updateProfile(user, {
          displayName
        });
      }

      if (email && email !== user.email) {
        await updateEmail(user, email);
      }

      const updatedUser = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        phone: phone || null
      };

      localStorage.setItem(
        'user',
        JSON.stringify(updatedUser)
      );

      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: updatedUser
      });

      return {
        success: true
      };
    } catch (error) {
      console.error('Update profile error:', error);

      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: error.message || 'Failed to update profile'
      });

      return {
        success: false,
        error: error.message
      };
    } finally {
      dispatch({
        type: AUTH_ACTIONS.SET_LOADING,
        payload: false
      });
    }
  };

  const changePassword = async (
    currentPassword,
    newPassword
  ) => {
    try {
      dispatch({
        type: AUTH_ACTIONS.SET_LOADING,
        payload: true
      });

      dispatch({
        type: AUTH_ACTIONS.CLEAR_ERROR
      });

      const user = auth.currentUser;

      if (!user) {
        throw new Error('Not authenticated');
      }

      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );

      await reauthenticateWithCredential(
        user,
        credential
      );

      await updatePassword(
        user,
        newPassword
      );

      return {
        success: true
      };
    } catch (error) {
      console.error('Change password error:', error);

      const message =
        error.code === 'auth/wrong-password'
          ? 'Current password is incorrect'
          : error.message || 'Failed to update password';

      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: message
      });

      return {
        success: false,
        error: message
      };
    } finally {
      dispatch({
        type: AUTH_ACTIONS.SET_LOADING,
        payload: false
      });
    }
  };

  const value = {
    ...state,
    login,
    logout,
    clearError,
    getToken,
    updateProfileDetails,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider'
    );
  }

  return context;
};